import React, { useMemo } from "react";
import { Loader2, Sparkles, Ticket, Trash2, Wallet, X } from "lucide-react";
import { TeamLogo } from "../bondhouse/FixtureStatus";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  combinedOdds,
  fmtOdd,
  formatOddPickLabel,
  formatOddPickMeta,
  minSelectionsFor,
  minStakeFor,
  slipFixtureId,
} from "../../utils/bettingDeskHelpers";

const buildSerial = (slip, house) => {
  const seed = `${slip?._id || house?._id || "bb"}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const code = Math.abs(hash).toString(36).toUpperCase().slice(0, 6).padEnd(6, "0");
  return `BB·${code.slice(0, 3)}·${code.slice(3, 6)}`;
};

const BetSlipPanel = ({
  house,
  slip,
  userCash,
  busy,
  onRemove,
  onPlaceClick,
  onClose,
  showClose = false,
}) => {
  const legs = useMemo(() => (Array.isArray(slip?.fixtures) ? slip.fixtures : []), [slip]);
  const minLegs = minSelectionsFor(house);
  const minStake = minStakeFor(house);
  const totalOdds = combinedOdds(slip);
  const legsOk = legs.length >= minLegs;
  const progress = Math.min(legs.length / minLegs, 1);
  const serial = useMemo(() => buildSerial(slip, house), [slip, house]);
  const oddsKey = `${legs.length}-${totalOdds.toFixed(3)}`;

  return (
    <aside className="pbd-slip">
      <header className="pbd-slip-head">
        <div className="pbd-slip-head-left">
          <Ticket size={14} />
          <div>
            <p className="pbd-slip-kicker">Coupon · {serial}</p>
            <h2 className="pbd-slip-title">Your slip</h2>
          </div>
        </div>
        <div className="pbd-slip-head-right">
          <div className="pbd-slip-wallet">
            <Wallet size={11} />
            <span className="tabular-nums">{formatCash(userCash, { compact: true })}</span>
          </div>
          {showClose ? (
            <button
              type="button"
              className="pbd-slip-close"
              onClick={onClose}
              aria-label="Close slip"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </header>

      <div className="pbd-slip-progress">
        <div className="pbd-slip-progress-track">
          <span className="pbd-slip-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <p>
          {legs.length} / {minLegs} legs
          {!legsOk ? ` · need ${minLegs - legs.length} more` : " · ready to stake"}
        </p>
      </div>

      <div className="pbd-slip-legs">
        {legs.length === 0 ? (
          <div className="pbd-slip-empty">
            <Sparkles size={16} />
            <p>Pick from the program.</p>
            <span>Tap 1 · X · 2 or open markets.</span>
          </div>
        ) : (
          legs.map((leg) => {
            const fx = leg.fixture;
            const fid = slipFixtureId(leg);
            const pick = leg.selectedOdd;
            return (
              <div key={fid} className="pbd-slip-leg">
                <div className="pbd-slip-leg-teams">
                  <TeamLogo team={fx?.teams?.home} size="sm" />
                  <span className="truncate">{fx?.teams?.home?.name || "Home"}</span>
                  <span className="pbd-slip-leg-vs">vs</span>
                  <TeamLogo team={fx?.teams?.away} size="sm" />
                  <span className="truncate">{fx?.teams?.away?.name || "Away"}</span>
                </div>
                <div className="pbd-slip-leg-pick">
                  <div>
                    <strong>{formatOddPickLabel(pick)}</strong>
                    <span>{formatOddPickMeta(pick)}</span>
                  </div>
                  <div className="pbd-slip-leg-actions">
                    <em className="tabular-nums">{fmtOdd(pick?.value)}</em>
                    <button
                      type="button"
                      className="pbd-slip-leg-remove"
                      disabled={busy}
                      aria-label="Remove leg"
                      onClick={() => onRemove?.(fid)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <footer className="pbd-slip-foot">
        <div className="pbd-slip-odds">
          <span>Combined odds · ×{legs.length || 0}</span>
          <strong key={oddsKey} className="tabular-nums pbd-slip-odds-value">
            {legs.length ? fmtOdd(totalOdds) : "—"}
          </strong>
        </div>
        {minStake > 0 ? (
          <p className="pbd-slip-hint">Min stake {formatCash(minStake, { compact: true })}</p>
        ) : null}
        <button
          type="button"
          className="pbd-slip-place"
          disabled={!legsOk || busy}
          onClick={onPlaceClick}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          {legsOk ? "Enter stake & place" : `Add ${minLegs - legs.length} more leg${minLegs - legs.length === 1 ? "" : "s"}`}
        </button>
      </footer>
    </aside>
  );
};

export default BetSlipPanel;
